<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="2" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Edit Prescription</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Modify prescription details, medications, and dosage information
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button -->
                    <v-btn
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-2"
                      small
                    >
                      <v-icon left>{{ isEditMode ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
                      {{ isEditMode ? 'Read Only' : 'Edit Mode' }}
                    </v-btn>
                    
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Prescriptions
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Form Header -->
            <v-card-title class="form-header py-1 px-2">
              <h2 class="form-title">Prescription Information</h2>
              <v-spacer></v-spacer>
              <v-chip 
                :color="isEditMode ? 'success' : 'warning'" 
                :outlined="!isEditMode" 
                x-small
              >
                <v-icon left x-small>{{ isEditMode ? 'mdi-pencil' : 'mdi-eye' }}</v-icon>
                {{ isEditMode ? 'Edit Mode' : 'Read Only Mode' }}
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>
            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <div v-if="!prescription && !isLoading" class="text-center">
                <v-icon large color="error">mdi-alert-circle</v-icon>
                <p class="mt-2">Prescription not found</p>
                <v-btn color="primary" @click="$router.back()">Go Back</v-btn>
              </div>
              <div v-else>
                <v-row>
                  <v-col sm="6">
                    <v-autocomplete
                      v-model="form.consultation_id"
                      :items="consultations"
                      outlined
                      label="Consultation (Optional)"
                      prepend-inner-icon="mdi-medical-bag"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :clearable="isEditMode"
                      :error-messages="errors.consultation_id"
                      hide-details="auto"
                      @input="onConsultationChange"
                    ></v-autocomplete>
                  </v-col>
                  <v-col sm="6">
                    <v-autocomplete
                      v-model="form.doctor_id"
                      :items="doctors"
                      outlined
                      label="Doctor"
                      prepend-inner-icon="mdi-doctor"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :clearable="isEditMode"
                      :error-messages="errors.doctor_id"
                      hide-details="auto"
                    ></v-autocomplete>
                  </v-col>
                  <v-col sm="6">
                    <v-autocomplete
                      v-model="form.patient_id"
                      :items="patients"
                      outlined
                      label="Patient"
                      prepend-inner-icon="mdi-account-heart"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :clearable="isEditMode"
                      :error-messages="errors.patient_id"
                      hide-details="auto"
                    ></v-autocomplete>
                  </v-col>
                  <v-col sm="6">
                    <v-switch
                      class="mt-0 ml-5"
                      v-model="form.is_active"
                      inset
                      :disabled="!isEditMode"
                      :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
                    ></v-switch>
                  </v-col>
                  <v-col sm="6" v-if="prescription && prescription.signature_image_path">
                    <div class="d-flex align-center" style="gap:8px;">
                      <v-icon color="grey darken-1">mdi-draw</v-icon>
                      <span class="text-body-2 grey--text text--darken-1">Doctor Signature</span>
                      <v-btn x-small color="info" outlined @click="signatureViewerDialog = true">
                        <v-icon left x-small>mdi-eye</v-icon> View
                      </v-btn>
                    </div>
                  </v-col>
                  <v-col sm="12">
                    <v-textarea
                      v-model="form.symptoms"
                      outlined
                      label="Symptoms *"
                      prepend-inner-icon="mdi-account-injury"
                      rows="3"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :error-messages="errors.symptoms"
                      placeholder="Describe the patient's symptoms..."
                      auto-grow
                      hide-details="auto"
                      :hint="isEditMode ? 'Describe the patient\'s current symptoms and complaints' : ''"
                    ></v-textarea>
                  </v-col>
                  <v-col sm="12">
                    <v-textarea
                      v-model="form.diagnosis"
                      outlined
                      label="Diagnosis *"
                      prepend-inner-icon="mdi-clipboard-pulse"
                      rows="3"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :error-messages="errors.diagnosis"
                      placeholder="Provide the medical diagnosis..."
                      auto-grow
                      hide-details="auto"
                      :hint="isEditMode ? 'Provide the medical diagnosis based on symptoms and examination' : ''"
                    ></v-textarea>
                  </v-col>
                  <v-col sm="12">
                    <v-textarea
                      v-model="form.tips"
                      outlined
                      label="Medical Tips & Recommendations"
                      prepend-inner-icon="mdi-lightbulb-on"
                      rows="4"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :error-messages="errors.tips"
                      placeholder="Provide medical tips, recommendations, and care instructions..."
                      auto-grow
                      hide-details="auto"
                      :hint="isEditMode ? 'Provide additional care instructions and lifestyle recommendations' : ''"
                    ></v-textarea>
                  </v-col>
                </v-row>

                <!-- Prescription Items Section -->
                <v-divider class="mt-4 mb-4"></v-divider>
                <v-card class="mb-4" outlined>
                  <v-card-title class="d-flex justify-space-between align-center">
                    <div>
                      <v-icon left color="success">mdi-pill</v-icon>
                      Prescription Items ({{ form.prescription_items.length }})
                    </div>
                    <v-btn
                      color="success"
                      small
                      @click="addPrescriptionItem"
                      :disabled="form.prescription_items.length >= 20 || !isEditMode"
                      v-if="isEditMode"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Add Item
                    </v-btn>
                  </v-card-title>
                  <v-card-text>
                    <div v-if="form.prescription_items.length === 0" class="text-center py-4 text-grey">
                      <v-icon large color="grey lighten-2">mdi-pill</v-icon>
                      <p class="mt-2">No prescription items added yet. Click "Add Item" to get started.</p>
                    </div>
                    
                    <v-row
                      v-for="(item, index) in form.prescription_items"
                      :key="item.id || index"
                      class="mb-4"
                    >
                      <v-col cols="12">
                        <v-card outlined class="prescription-item-card">
                          <v-card-title class="py-2 px-3">
                            <span class="text-subtitle-2">Medication #{{ index + 1 }}</span>
                            <v-spacer></v-spacer>
                            <v-btn
                              icon
                              small
                              color="error"
                              @click="removePrescriptionItem(index)"
                              :disabled="form.prescription_items.length <= 1 || !isEditMode"
                              v-if="isEditMode"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </v-card-title>
                          <v-card-text class="pt-2">
                            <v-row>
                              <v-col sm="3">
                                <v-autocomplete
                                  v-model="item.product_id"
                                  :items="products"
                                  item-text="name"
                                  item-value="id"
                                  outlined
                                  dense
                                  label="Product *"
                                  prepend-inner-icon="mdi-pill"
                                  :disabled="!isEditMode"
                                  :readonly="!isEditMode"
                                  :error-messages="getPrescriptionItemError(index, 'product_id')"
                                  hide-details="auto"
                                ></v-autocomplete>
                              </v-col>
                              <v-col sm="2">
                                <v-autocomplete
                                  v-model="item.mode_id"
                                  :items="modes"
                                  item-text="description"
                                  item-value="id"
                                  outlined
                                  dense
                                  label="Mode"
                                  prepend-inner-icon="mdi-format-list-bulleted"
                                  :disabled="!isEditMode"
                                  :readonly="!isEditMode"
                                  :error-messages="getPrescriptionItemError(index, 'mode_id')"
                                  hide-details="auto"
                                ></v-autocomplete>
                              </v-col>
                              <v-col sm="2">
                                <v-text-field
                                  v-model.number="item.dosage"
                                  outlined
                                  dense
                                  label="Dosage (mg)"
                                  prepend-inner-icon="mdi-medical-bag"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  :disabled="!isEditMode"
                                  :readonly="!isEditMode"
                                  :error-messages="getPrescriptionItemError(index, 'dosage')"
                                  hide-details="auto"
                                ></v-text-field>
                              </v-col>
                              <v-col sm="2">
                                <v-text-field
                                  v-model="item.frequency"
                                  outlined
                                  dense
                                  label="Frequency"
                                  prepend-inner-icon="mdi-clock"
                                  placeholder="e.g., 3 times daily"
                                  :disabled="!isEditMode"
                                  :readonly="!isEditMode"
                                  :error-messages="getPrescriptionItemError(index, 'frequency')"
                                  hide-details="auto"
                                ></v-text-field>
                              </v-col>
                              <v-col sm="3">
                                <v-text-field
                                  v-model="item.duration"
                                  outlined
                                  dense
                                  label="Duration"
                                  prepend-inner-icon="mdi-calendar"
                                  placeholder="e.g., 7 days"
                                  :disabled="!isEditMode"
                                  :readonly="!isEditMode"
                                  :error-messages="getPrescriptionItemError(index, 'duration')"
                                  hide-details="auto"
                                ></v-text-field>
                              </v-col>
                              <v-col sm="12">
                                <v-textarea
                                  v-model="item.notes"
                                  outlined
                                  dense
                                  label="Special Instructions / Notes"
                                  prepend-inner-icon="mdi-note-text"
                                  rows="2"
                                  placeholder="Special instructions for this medication..."
                                  :disabled="!isEditMode"
                                  :readonly="!isEditMode"
                                  :error-messages="getPrescriptionItemError(index, 'notes')"
                                  auto-grow
                                  hide-details="auto"
                                ></v-textarea>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>

                  </v-card-text>
                </v-card>

            <!-- Prescription Information Card -->
            <v-card class="mt-4" outlined>
              <v-card-title>
                <v-icon left>mdi-information</v-icon>
                Prescription Information
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col sm="3">
                    <strong>Created:</strong><br />
                    {{ formatDate(prescription.created_at) }}
                  </v-col>
                  <v-col sm="3">
                    <strong>Last Updated:</strong><br />
                    {{ formatDate(prescription.updated_at) }}
                  </v-col>
                  <v-col sm="3">
                    <strong>Created By:</strong><br />
                    {{
                      prescription.created_by
                        ? prescription.created_by.user_name
                        : "N/A"
                    }}
                  </v-col>
                  <v-col sm="3">
                    <strong>Status:</strong><br />
                    <v-chip
                      small
                      :color="form.is_active ? 'success' : 'error'"
                      text-color="white"
                    >
                      {{ form.is_active ? "Active" : "Inactive" }}
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

              
              <!-- Form Actions Footer -->
              <v-divider></v-divider>
              <v-card-actions class="form-actions pa-2" v-show="isEditMode">
                <v-row class="w-100 ma-0" align="center">
                  <v-col cols="12" md="6" class="pa-0">
                    <div class="d-flex align-center">
                      <v-chip 
                        v-if="form.prescription_items.length === 0"
                        small 
                        color="error" 
                        text-color="white"
                        class="mr-3"
                      >
                        <v-icon left x-small>mdi-alert-circle</v-icon>
                        Please add at least one prescription item
                      </v-chip>
                      <span class="text-caption grey--text" v-else>
                        {{ form.prescription_items.length }} medication(s) prescribed
                      </span>
                    </div>
                  </v-col>
                  
                  <v-col cols="12" md="6" class="pa-0">
                    <div class="d-flex justify-end">
                      <v-btn
                        color="error"
                        outlined
                        class="mr-3 action-button"
                        @click="confirmDelete"
                        :disabled="isSaving"
                      >
                        <v-icon left>mdi-delete</v-icon>
                        Delete
                      </v-btn>
                      <v-btn
                        color="grey"
                        outlined
                        class="mr-3 action-button"
                        @click="$router.back()"
                        :disabled="isSaving"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        color="primary"
                        class="action-button"
                        @click="updateData"
                        :loading="isSaving"
                        :disabled="isSaving || form.prescription_items.length === 0"
                      >
                        <v-icon left>mdi-content-save</v-icon>
                        Update Prescription
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-card-actions>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

        <!-- Loading State -->
        <v-row v-if="isLoading">
          <v-col cols="12">
            <v-card elevation="3" class="form-card">
              <v-card-text class="py-12 text-center">
                <health-loader message="Loading prescription details..." />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Prescription updated successfully.</h3>
                      <br />
                      <v-btn color="success" @click="showSuccessDialog = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: red; font-size: 90px; width: 200px !important"
                >
                  mdi-alert-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: red">Confirm Delete</h1>
                      <br />
                      <h3>
                        Are you sure you want to delete this prescription?
                      </h3>
                      <br />
                      <v-btn
                        color="error"
                        @click="deleteData"
                        :loading="isDeleting"
                        class="mr-4"
                      >
                        Yes, Delete
                      </v-btn>
                      <v-btn
                        color="secondary"
                        @click="showDeleteDialog = false"
                        :disabled="isDeleting"
                      >
                        Cancel
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <FileViewer
        v-model="signatureViewerDialog"
        :url="prescription && prescription.signature_image_path ? mediaUrl + prescription.signature_image_path : ''"
        title="Doctor Signature"
      />
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import { FileViewer } from "@/components";

export default {
  name: "PrescriptionEditPage",
  components: { FileViewer },
  data() {
    return {
      prescription: null,
      signatureViewerDialog: false,
      form: {
        consultation_id: null,
        doctor_id: null,
        patient_id: null,
        symptoms: "",
        diagnosis: "",
        tips: "",
        is_active: true,
        prescription_items: [
          {
            product_id: null,
            mode_id: null,
            dosage: null,
            frequency: "",
            duration: "",
            notes: "",
            is_active: true,
          },
        ],
      },
      errors: {},
      isLoading: true,
      isSaving: false,
      isDeleting: false,
      showSuccessDialog: false,
      showDeleteDialog: false,
      consultations: [],
      doctors: [],
      patients: [],
      products: [],
      modes: [],
      isEditMode: false,
    };
  },
  computed: {
    mediaUrl() {
      return this.$store.getters["settings/getMediaUrl"];
    },
  },
  mounted() {
    this.fetchPrescription();
    this.getMasters();
  },
  methods: {
    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      // Show a message to the user about the mode change
      if (this.isEditMode) {
        this.$toast.success("Edit mode enabled. You can now modify the form.");
      } else {
        this.$toast.info("Read-only mode enabled. Form is now protected from changes.");
      }
    },

    async fetchPrescription() {
      this.isLoading = true;
      try {
        const prescriptionId = this.$route.params.id;
        const response = await axios.get(`/prescriptions/${prescriptionId}`);

        if (response.data.success) {
          this.prescription = response.data.data;
          this.form = {
            consultation_id: this.prescription.consultation_id,
            doctor_id: this.prescription.doctor_id,
            patient_id: this.prescription.patient_id,
            symptoms: this.prescription.symptoms || "",
            diagnosis: this.prescription.diagnosis || "",
            tips: this.prescription.tips || "",
            is_active: this.prescription.is_active,
            prescription_items: this.prescription.prescription_items && this.prescription.prescription_items.length > 0
              ? this.prescription.prescription_items.map(item => ({
                  id: item.id,
                  product_id: item.product_id,
                  mode_id: item.mode_id,
                  dosage: item.dosage,
                  frequency: item.frequency || "",
                  duration: item.duration || "",
                  notes: item.notes || "",
                  is_active: item.is_active,
                }))
              : [
                  {
                    product_id: null,
                    mode_id: null,
                    dosage: null,
                    frequency: "",
                    duration: "",
                    notes: "",
                    is_active: true,
                  },
                ],
          };
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Prescription not found",
          });
        }
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 404) {
          this.$store.dispatch("errors/setError", {
            message: "Prescription not found",
          });
        } else if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error loading prescription data",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    async getMasters() {
      try {
        const response = await axios.get("masters?context=prescription_items");
        if (response.data?.users) {
          this.doctors = response.data.users
            .filter((user) => user.position?.name?.toLowerCase() === "doctor")
            .map((user) => ({
              text: user.first_name,
              value: user.id,
            }));
        }
        if (response.data?.users) {
          this.patients = response.data.users
            .filter((user) => user.position?.name?.toLowerCase() === "patient")
            .map((user) => ({
              text: user.first_name,
              value: user.id,
            }));
        }
        if (response.data?.consultations) {
          this.consultations = response.data.consultations.map(
            (consultation) => ({
              text: consultation.title,
              value: consultation.id,
            })
          );
        }
        if (response.data?.products) {
          this.products = response.data.products;
        }
        if (response.data?.modes) {
          this.modes = response.data.modes.map(mode => ({
            id: mode.id,
            name: mode.description,
            value: mode.id,
            text: mode.description
          }));
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },

    onConsultationChange() {
      // If a consultation is selected, auto-fill doctor and patient if available
      if (this.form.consultation_id) {
        const selectedConsultation = this.consultations.find(
          (c) => c.id === this.form.consultation_id
        );
        if (selectedConsultation) {
          this.form.doctor_id = selectedConsultation.doctor_id || null;
          this.form.patient_id = selectedConsultation.patient_id || null;
        }
      }
    },

    async updateData() {
      this.isSaving = true;
      this.errors = {};

      try {
        const prescriptionData = {
          consultation_id: this.form.consultation_id,
          doctor_id: this.form.doctor_id,
          patient_id: this.form.patient_id,
          symptoms: this.form.symptoms,
          diagnosis: this.form.diagnosis,
          tips: this.form.tips,
          is_active: this.form.is_active,
          prescription_items: this.form.prescription_items.filter(item => 
            item.product_id || item.dosage || item.frequency || item.duration || item.notes
          ),
        };

        const prescriptionId = this.$route.params.id;
        const response = await axios.patch(
          `/prescriptions/${prescriptionId}`,
          prescriptionData
        );

        if (response.data.success) {
          this.showSuccessDialog = true;
          this.prescription = response.data.data;
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while updating the prescription.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            this.$router.push("/login");
          } else {
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while updating the prescription.",
            });
          }
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    confirmDelete() {
      this.showDeleteDialog = true;
    },

    async deleteData() {
      this.isDeleting = true;

      try {
        const prescriptionId = this.$route.params.id;
        const response = await axios.post(
          `/prescriptions/delete/${prescriptionId}`
        );

        if (response.data.success) {
          this.showDeleteDialog = false;
          this.$router.push("/prescriptions");
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while deleting the prescription.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "An error occurred while deleting the prescription.",
          });
        }
      } finally {
        this.isDeleting = false;
      }
    },

    // Prescription Items Methods
    addPrescriptionItem() {
      if (this.form.prescription_items.length < 20 && this.isEditMode) {
        this.form.prescription_items.push({
          product_id: null,
          mode_id: null,
          dosage: null,
          frequency: "",
          duration: "",
          notes: "",
          is_active: true,
        });
      }
    },

    removePrescriptionItem(index) {
      if (this.form.prescription_items.length > 1) {
        this.form.prescription_items.splice(index, 1);
      }
    },

    getPrescriptionItemError(index, field) {
      if (this.errors && this.errors[`prescription_items.${index}.${field}`]) {
        return this.errors[`prescription_items.${index}.${field}`];
      }
      return [];
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },
  },
};
</script>

<style scoped>
/* Professional Page Header */
.page-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-left: 4px solid var(--v-primary-base);
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

/* Professional Form Card */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.form-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--v-primary-base);
  margin: 0;
}

.form-content {
  background: #ffffff;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Prescription Item Cards */
.prescription-item-card {
  transition: all 0.2s ease;
  border-left: 3px solid #1976d2;
}

.prescription-item-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.v-card-title {
  font-weight: bold;
  font-size: 1.5rem;
}

.v-text-field.outlined .v-input__control {
  min-height: 48px;
}

.v-textarea.outlined .v-input__control {
  min-height: 120px;
}
</style>